import type { FC, FocusEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Add as AddIcon,
  Create as EditIcon,
  DateRange as CalendarIcon,
  Error as ErrorIcon,
  Info,
} from '@mui/icons-material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import { MobileDatePicker as DatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import clsx from 'clsx';
import { add, differenceInMinutes, format, parse } from 'date-fns';
import type { FormikHelpers, FormikProps } from 'formik';
import { Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';
import * as Yup from 'yup';

import { COMPANY_NAME } from 'src/constants';
import { useConfirm } from 'src/contexts/ConfirmContext';
import { TestIds } from 'src/testIds';
import type { Appointment, AppointmentRecordPostData } from 'src/types';
import {
  getCareBoxInfo,
  getFirstInterviewInfo,
  postCareBoxOrder,
  useGetFeatureFlag,
} from 'src/services/api/api';
import i18n from 'src/services/i18n/i18n';
import { routes } from 'src/services/routing';
import {
  actions as appointmentRecordsActions,
  getters as appointmentRecordsGetters,
} from 'src/services/state/AppointmentRecords';
import { getters as customersGetters } from 'src/services/state/Customers';
import UserState from 'src/services/state/User';
import Alert from 'src/components/Alert/Alert';
import SmsNotificationPhoneDialog from 'src/components/AppointmentRecordForm/SmsNotificationPhoneDialog';
import Button from 'src/components/Button/Button';
import { EndTimeSelect } from 'src/components/EndTimeSelect/EndTimeSelect';
import Form from 'src/components/Form/Form';
import Image from 'src/components/Image/Image';
import { NeedsAssessmentModal } from 'src/components/NeedsAssessment/NeedsAssessmentModal';
import type { SignatureProps } from 'src/components/Signature/Signature';
import Signature from 'src/components/Signature/Signature';
import { StartTimeSelect } from 'src/components/StartTimeSelect/StartTimeSelect';
import TextField from 'src/components/TextField/TextField';

import useStyles from 'src/components/AppointmentRecordForm/AppointmentRecordForm.styles';

const {
  startTimeField: startTimeFieldTestId,
  endTimeField: endTimeFieldTestId,
} = TestIds.components;
const ComponentTestIds = TestIds.components.appointmentRecordForm;

export interface AppointmentRecordFormProps {
  afterSubmit?: () => void;
  appointment: Appointment;
  className?: string;
  initialValues?: {
    wasFirstInterview?: boolean;
  };
}

interface Values extends AppointmentRecordPostData {
  orderCareBox?: boolean;
  submit?: string;
  gdpr_main_consent: boolean;
  gdpr_consent_partner_companies: boolean;
}

/* eslint-disable sort-keys-fix/sort-keys-fix */
const validationSchema = Yup.object().shape({
  dateDay: Yup.date()
    .nullable()
    .required(i18n.t('General.Form.dateDayRequired'))
    // Note: perform date validation in .test instead of simply using .max(lastDayOfMonth), since that variable
    // otherwise would be out-of-date after some time (see ticket #558)
    .test(
      'validate-same-month',
      i18n.t('AppointmentRecordForm.Fields.DateDay.canNotBeFollowingMonth'),
      (value, context) => {
        if (!value) {
          return false;
        }

        const today = new Date();
        const lastDayOfMonth = new Date();
        lastDayOfMonth.setFullYear(
          today.getFullYear(),
          today.getMonth() + 1,
          0,
        );
        return value < lastDayOfMonth;
      },
    )
    .test(
      'date-not-too-past',
      i18n.t('AppointmentRecordForm.Fields.StartTime.tooFarInThePast'),
      (value) => {
        const today = new Date();
        const pastMonthStart = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1,
        );
        return !value || value >= pastMonthStart;
      },
    ),
  startTime: Yup.date()
    .nullable()
    .required(i18n.t('AppointmentRecordForm.Fields.StartTime.required')),
  endTime: Yup.date()
    .nullable()
    .required(i18n.t('AppointmentRecordForm.Fields.EndTime.required'))
    .when(
      'startTime',
      (startTime, schema) =>
        startTime &&
        schema.min(
          add(startTime, { minutes: 1 }),
          i18n.t('AppointmentRecordForm.Fields.EndTime.mustBeAfterStartTime'),
        ),
    )
    .test(
      'first-interview-duration',
      i18n.t('AppointmentRecordForm.Fields.EndTime.unsuitableDuration'),
      (endTime, context) => {
        const { startTime, wasFirstInterview } = context.parent;
        if (startTime && endTime && wasFirstInterview) {
          const duration = differenceInMinutes(
            new Date(endTime),
            new Date(startTime),
          );

          if (duration !== 30 && duration !== 60) {
            return false;
          }

          return true;
        }

        return true;
      },
    ),

  wasFirstInterview: Yup.boolean(),

  gdpr_main_consent: Yup.boolean().when('wasFirstInterview', {
    is: true,
    then: Yup.boolean().oneOf([true], 'Einwilligung erforderlich'),
  }),

  travelDistanceKm: Yup.number()
    .min(0, i18n.t('AppointmentRecordForm.Fields.TravelDistanceKm.positive'))
    .max(
      100,
      i18n.t('AppointmentRecordForm.Fields.TravelDistanceKm.maxDistance'),
    )
    .integer(i18n.t('AppointmentRecordForm.Fields.TravelDistanceKm.integer'))
    .required(i18n.t('AppointmentRecordForm.Fields.TravelDistanceKm.required')),

  signatureDopp: Yup.string().when('wasFirstInterview', {
    is: true,
    then: Yup.string().required(
      i18n.t('AppointmentRecordForm.Fields.SignatureDopp.required'),
    ),
  }),
  signatureRecordCustomer: Yup.string().required(
    i18n.t('AppointmentRecordForm.Fields.SignatureRecordCustomer.required'),
  ),
  signatureRecordEmployee: Yup.string().required(
    i18n.t('AppointmentRecordForm.Fields.SignatureRecordEmployee.required'),
  ),
});
/* eslint-enable */

const initialModalOptions = {
  onSuccess: () => { },
  open: false,
  signatory: 'signatory',
};
const careBoxOptions = (canOrderCareBox: boolean) => [
  { disabled: !canOrderCareBox, label: 'Beispielpaket 1', value: 1 },
  { disabled: !canOrderCareBox, label: 'Beispielpaket 2', value: 2 },
  { disabled: !canOrderCareBox, label: 'Beispielpaket 3', value: 3 },
  { disabled: !canOrderCareBox, label: 'Beispielpaket 4', value: 4 },
  { disabled: false, label: 'Individuelles Paket', value: 5 },
];

export const AppointmentRecordForm: FC<AppointmentRecordFormProps> = ({
  afterSubmit: _afterSubmit,
  appointment,
  className,
  ...props
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation<any>();
  const confirm = useConfirm();
  const confirmStartTime = useConfirm();
  const confirmEndTime = useConfirm();
  const canConductFirstInterviews = UserState.user?.canConductFirstInterviews;
  const customer = customersGetters.getCustomerById(appointment.customerId);
  const formikRef = useRef<FormikProps<Values>>(null);
  const signatoryFor = location.hash;
  const [modalOptions, setModalOptions] =
    useState<SignatureProps>(initialModalOptions);
  const [needsAssessmentFormSubmitted, setNeedsAssessmentFormSubmitted] =
    useState(false);
  const [openNeedsAssessmentForm, setOpenNeedsAssessmentForm] = useState(false);
  const [shouldOrderCareBox, setShouldOrderCareBox] = useState(false);
  const [shouldDisableCareBox, setShouldDisableCareBox] = useState(false);
  const [showInfoText, setShowInfoText] = useState(false);
  const [selectedCareBox, setSelectedCareBox] = useState<number | null>(null);
  const [signatureCareBox, setSignatureCareBox] = useState<string | null>(null);
  const [isOrderingCareBox, setIsOrderingCareBox] = useState(false);
  const [futureWarningAcknowledged, setFutureWarningAcknowledged] =
    useState(false);
  const [openSmsNotificationPhoneDialog, setOpenSmsNotificationPhoneDialog] =
    useState(false);
  const afterSubmit =
    _afterSubmit || (() => history.push(routes.app.routes.home.path));
  const today = new Date();
  const todayDateString = format(today, 'yyyy-MM-dd');
  const appointmentId = appointment.id;
  const recurrenceIndex = appointment.recurrenceIndex;
  const dateDay = appointment.dateDay;
  const endTime = parse(appointment.endTime, 'HH:mm:ss', today).toString();
  const startTime = parse(appointment.startTime, 'HH:mm:ss', today).toString();
  const appointmentDate = new Date(dateDay);
  const isAllowedToOrderCareBox = useGetFeatureFlag('can-order-care-box');
  const agreedActivitiesEnabled = useGetFeatureFlag(
    'customer-agreed-activities-app',
  );
  const agreedActivitiesHourlyRateEnabled = useGetFeatureFlag(
    'customer-agreed-activities-hourly-rate-app',
  );
  const smsNotificationPhoneEnabled = useGetFeatureFlag(
    'sms-notification-phone-app',
  );
  const isSmsNotificationPhoneVerified = Boolean(
    customer?.smsNotificationPhone && customer?.smsNotificationPhoneVerifiedAt,
  );

  const isAppointmentInFuture = dateDay > todayDateString;
  const showFutureWarning = isAppointmentInFuture && !futureWarningAcknowledged;

  // should only be signable if it's not too far in the future, not next month
  const isSignable =
    appointmentDate < new Date(today.getFullYear(), today.getMonth() + 1, 1);

  // Get a potentially pending appointment record for this appointment.
  let appointmentRecordPending =
    appointmentRecordsGetters.getAppointmentRecordPending(
      appointment.id,
      recurrenceIndex,
    );

  // Format the start- and endTime values of the AppointmentRecordPending to fit the form inputs.
  if (appointmentRecordPending) {
    appointmentRecordPending = {
      ...appointmentRecordPending,
      endTime: parse(
        appointmentRecordPending.endTime,
        'HH:mm',
        today,
      ).toString(),
      startTime: parse(
        appointmentRecordPending.startTime,
        'HH:mm',
        today,
      ).toString(),
    };
  }

  // Use the pending appointment record or the appointments data to pre-fill the form.
  const initialValues: Values = appointmentRecordPending || {
    appointmentId,
    dateDay,
    endTime,
    gdpr_consent_partner_companies: true,
    gdpr_main_consent: false,
    orderCareBox: false,
    recurrenceIndex,
    signatureCareBox: '',
    signatureDoa: '',
    signatureDopp: '',
    signatureRecordCustomer: '',
    signatureRecordEmployee: '',
    startTime,
    submit: '',
    travelDistanceKm: '' as unknown as number,
    wasFirstInterview: false,
  };

  /**
   * Add hash with the provided signatory to the location and keep the stackRoot while navigating.
   */
  const goToSignatureModal = (signatory: string) => {
    history.push(`${location.pathname}${location.search}#${signatory}`, {
      stackRoot: location.state.stackRoot,
    });
  };

  /**
   * Scrolls to the first third of the select drop down if no item has been selected yet.
   */
  const handleSelectFocus = (
    event: FocusEvent<HTMLDivElement>,
    value?: string,
  ) => {
    if (value) return;

    const menuElement = event.target.closest('[class*="MuiList-root"]')
      ?.parentElement;

    // Only scroll if the drop down was not scrolled already to prevent
    // strange behavior caused by bubbling focus events.
    if (menuElement && !menuElement.scrollTop) {
      menuElement.scrollTo({ top: menuElement?.scrollHeight / 3 });
    }
  };

  /**
   * Format endTimeSteps depending on the startTime value, handles StartTime
   * change and confirmation modal
   */
  const handleStartTimeChange = (
    event: SelectChangeEvent,
    initialStartTime?: string,
  ) => {
    const startTime = (event.target.value as string) ?? undefined;

    if (startTime !== initialStartTime) {
      confirmStartTime({
        description: t('AppointmentRecordForm.confirmStartTime'),
        hideCloseButton: true,
        onClose: { disableBackdropClick: true },
        primaryText: t('AppointmentRecordForm.confirmTime'),
        secondaryText: t('AppointmentRecordForm.rejectTime'),
        title: t('AppointmentRecordForm.modalTitleStartTime'),
      })
        .then(() => {
          formikRef.current?.setFieldValue('startTime', startTime);
        })
        .catch(() => {
          formikRef.current?.setFieldValue('startTime', initialStartTime);
        });
    } else {
      formikRef.current?.setFieldValue('startTime', startTime);
    }
  };

  /**
   * Handles EndTime change and confirmation modal
   */
  const handleEndTimeChange = (event: any, initialEndTime?: string) => {
    const endTime = (event.target.value as string) ?? undefined;

    if (endTime !== initialEndTime) {
      confirmEndTime({
        description: t('AppointmentRecordForm.confirmEndTime'),
        hideCloseButton: true,
        onClose: { disableBackdropClick: true },
        primaryText: t('AppointmentRecordForm.confirmTime'),
        secondaryText: t('AppointmentRecordForm.rejectTime'),
        title: t('AppointmentRecordForm.modalTitleEndTime'),
      })
        .then(() => {
          formikRef.current?.setFieldValue('endTime', endTime);
        })
        .catch(() => {
          formikRef.current?.setFieldValue('endTime', initialEndTime);
        });
    } else {
      formikRef.current?.setFieldValue('endTime', endTime);
    }
  };
  /**
   * Handles opening the signature modal with the appropriate texts for employees or customers.
   */
  const handleModal = (signatoryFor: string) => {
    if (!formikRef.current) return;

    const setFieldValue = formikRef.current.setFieldValue;
    const values = formikRef.current.values;
    const day = format(new Date(values.dateDay), 'dd.MM.yyyy');
    const duration =
      differenceInMinutes(
        new Date(values.endTime),
        new Date(values.startTime),
      ) / 60;

    let description;
    let open = false;
    let title;
    let signatory;

    switch (signatoryFor) {
      case 'signatureRecordCustomer':
        description = t(
          'AppointmentRecordForm.Fields.SignatureRecordCustomer.description',
          {
            count: duration,
            day,
          },
        );
        open = true;
        signatory = t('General.customer');
        title = t('AppointmentRecordForm.Fields.SignatureRecordCustomer.label');
        break;

      case 'signatureRecordEmployee':
        description = t(
          'AppointmentRecordForm.Fields.SignatureRecordEmployee.description',
        );
        open = true;
        signatory = t('General.employee');
        title = t('AppointmentRecordForm.Fields.SignatureRecordEmployee.label');
        break;

      case 'signatureDoa':
        description = t(
          'AppointmentRecordForm.Fields.SignatureDoa.description',
        );
        open = true;
        signatory = t('General.customer');
        title = t('AppointmentRecordForm.Fields.SignatureDoa.label');
        break;

      case 'signatureDopp':
        description = t(
          'AppointmentRecordForm.Fields.SignatureDopp.description',
        );
        open = true;
        signatory = t('General.customer');
        title = t('AppointmentRecordForm.Fields.SignatureDopp.label');
        break;

      default:
        description = '';
        open = false;
        signatory = '';
        title = '';
        break;
    }

    setModalOptions({
      ...modalOptions,
      description,
      onSuccess: (signature) => setFieldValue(signatoryFor, signature),
      open,
      signatory,
      title,
    });
  };

  /**
   * Prepare the form`s values and handle success and error cases after submitting them.
   */
  const handleSubmit = async (
    values: Values,
    { setErrors }: FormikHelpers<Values>,
  ) => {
    try {
      // Get user confirmation before submitting.
      await confirm({
        description: t('AppointmentRecordForm.submitDescription'),
        primaryText: t('AppointmentRecordForm.submit'),
        title: t('AppointmentRecordForm.submitTitle'),
      });

      const data = { ...values };
      delete data.submit;

      // Adjust time format
      data.endTime = format(new Date(data.endTime), 'HH:mm');
      data.startTime = format(new Date(data.startTime), 'HH:mm');

      // Wait for the request.
      await appointmentRecordsActions.createAppointmentRecord({ data });

      enqueueSnackbar(t('AppointmentRecordForm.successMessage'), {
        variant: 'success',
      });

      afterSubmit();
    } catch (error: any) {
      if (error === 'onClose' || error === 'onSecondary') return;

      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.[0] ||
        error.response?.data?.nonFieldErrors?.join(' - ') ||
        t('AppointmentRecordForm.couldNotCreate');

      setErrors({ submit: errorMessage });
    }
  };

  /**
   * On location hash change handle the signature modal accordingly.
   */
  useEffect(() => {
    // Use "substring(1)" to remove the "#".
    handleModal(signatoryFor.substring(1));
  }, [signatoryFor]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Formik
        initialValues={initialValues}
        innerRef={formikRef}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          isSubmitting,
          touched,
          values,
        }) => (
          <Form className={clsx(classes.root, className)} noValidate {...props}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h4">
                  {t('AppointmentRecordForm.title')}
                </Typography>
              </Grid>

              {showFutureWarning && (
                <Grid item xs={12}>
                  <Alert
                    severity="error"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => setFutureWarningAcknowledged(true)}
                      >
                        {t('AppointmentRecordForm.futureAppointmentConfirm')}
                      </Button>
                    }
                  >
                    {t('AppointmentRecordForm.futureAppointmentWarning')}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12} data-test-id={ComponentTestIds.dateDayField}>
                <DatePicker
                  closeOnSelect
                  inputFormat="dd | MM | yyyy"
                  renderInput={(props) => (
                    <TextField
                      helperText={touched.dateDay && errors.dateDay}
                      variant="outlined"
                      name="dateDay"
                      required
                      fullWidth
                      {...props}
                      error={!!(touched.dateDay && errors.dateDay)}
                    />
                  )}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                  label={t('General.Form.dateLabel')}
                  onChange={(value) =>
                    formikRef.current?.setFieldValue(
                      'dateDay',
                      format(new Date(value || ''), 'yyyy-MM-dd'),
                    )
                  }
                  value={values.dateDay}
                />
              </Grid>

              <Grid item xs={6}>
                <FormControl
                  data-test-id={startTimeFieldTestId}
                  error={!!(touched.startTime && errors.startTime)}
                  fullWidth
                  required
                  variant="outlined"
                >
                  <InputLabel id="startTime-label">
                    {t('General.Form.startTimeLabel')}
                  </InputLabel>
                  <StartTimeSelect
                    error={touched.startTime ? errors.startTime : undefined}
                    value={values.startTime}
                    handleBlur={handleBlur}
                    handleOnChange={(event: SelectChangeEvent<any>) =>
                      handleStartTimeChange(event, initialValues.startTime)
                    }
                    handleOnFocus={(event) =>
                      handleSelectFocus(event, values.startTime)
                    }
                    required
                  />
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <FormControl
                  data-test-id={endTimeFieldTestId}
                  error={!!(touched.endTime && errors.endTime)}
                  fullWidth
                  required
                  variant="outlined"
                >
                  <InputLabel id="endTime-label">
                    {t('General.Form.endTimeLabel')}
                  </InputLabel>
                  <EndTimeSelect
                    error={touched.endTime ? errors.endTime : undefined}
                    startTime={
                      values.startTime ? new Date(values.startTime) : undefined
                    }
                    value={values.endTime}
                    handleBlur={handleBlur}
                    handleOnChange={(event) => {
                      handleEndTimeChange(event, initialValues.endTime);
                    }}
                    handleOnFocus={(event) =>
                      handleSelectFocus(event, values.startTime)
                    }
                    hideDuration
                    required
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  data-test-id={ComponentTestIds.travelDistanceField}
                  error={
                    !!(touched.travelDistanceKm && errors.travelDistanceKm)
                  }
                  fullWidth
                  helperText={
                    touched.travelDistanceKm && errors.travelDistanceKm
                  }
                  label={t(
                    'AppointmentRecordForm.Fields.TravelDistanceKm.label',
                  )}
                  name="travelDistanceKm"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  required
                  type="number"
                  value={values.travelDistanceKm}
                  variant="outlined"
                />
              </Grid>

              {canConductFirstInterviews && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.wasFirstInterview}
                        color="success"
                        data-test-id={ComponentTestIds.wasFirstInterviewSwitch}
                        disabled={needsAssessmentFormSubmitted}
                        name="wasFirstInterview"
                        onChange={async (event) => {
                          const isChecked = event.target.checked;

                          if (!isChecked) {
                            formikRef.current?.setFieldValue(
                              'wasFirstInterview',
                              false,
                            );
                            return;
                          }

                          const start = values.startTime
                            ? new Date(values.startTime)
                            : null;
                          const end = values.endTime
                            ? new Date(values.endTime)
                            : null;

                          if (!start || !end) {
                            enqueueSnackbar(t('General.Error'), {
                              variant: 'error',
                            });
                            return;
                          }

                          const duration = differenceInMinutes(end, start);
                          if (duration !== 30 && duration !== 60) {
                            enqueueSnackbar(
                              t(
                                'AppointmentRecordForm.Fields.EndTime.unsuitableDuration',
                              ),
                              { variant: 'error' },
                            );
                            formikRef.current?.setFieldValue(
                              'wasFirstInterview',
                              false,
                            );
                            return;
                          }

                          if (customer?.id) {
                            try {
                              const response = await getFirstInterviewInfo({
                                customerId: customer.id,
                                date: format(
                                  new Date(values.dateDay),
                                  'yyyy-MM-dd',
                                ),
                              });

                              if (!response.data.valid) {
                                enqueueSnackbar(
                                  response.data.message ||
                                  t(
                                    'AppointmentRecordForm.Fields.WasFirstInterview.hadFirstInterview',
                                  ),
                                  { variant: 'error' },
                                );
                                formikRef.current?.setFieldValue(
                                  'wasFirstInterview',
                                  false,
                                );
                                return;
                              }
                            } catch (error) {
                              enqueueSnackbar(
                                t(
                                  'AppointmentRecordForm.Fields.WasFirstInterview.validationError',
                                ),
                                {
                                  variant: 'warning',
                                },
                              );
                            }
                          }
                          handleChange(event);
                          setOpenNeedsAssessmentForm(true);
                        }}
                      />
                    }
                    label={t(
                      'AppointmentRecordForm.Fields.WasFirstInterview.label',
                    )}
                    labelPlacement="start"
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  // data-test-id={ComponentTestIds.freeOfChargeAid.careBox}
                  name="orderCareBox"
                  control={
                    <Checkbox
                      color="primary"
                      checked={
                        Boolean(values?.orderCareBox) ||
                        (needsAssessmentFormSubmitted && shouldOrderCareBox)
                      }
                      disabled={
                        !customer?.canOrderCareBox || shouldDisableCareBox
                      }
                      onBlur={handleBlur}
                      onChange={(event) => {
                        handleChange(event);
                        setShouldOrderCareBox((prev) => !prev);
                      }}
                    />
                  }
                  label={
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      {t('NeedsAssessmentForm.FreeOfChargeAid.careBox')}
                      {!customer?.canOrderCareBox && (
                        <IconButton
                          children={<Info />}
                          color="warning"
                          onClick={() => {
                            values && setShowInfoText(!showInfoText);
                          }}
                        />
                      )}
                    </Box>
                  }
                  labelPlacement="end"
                />
                {showInfoText && (
                  <Box mt={3}>
                    <Alert severity="info">
                      {t(
                        'AppointmentRecordForm.Fields.CareBoxOrder.description',
                      )}
                    </Alert>
                  </Box>
                )}
              </Grid>

              {shouldOrderCareBox && (
                <>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="care-box-select-label">
                        {t(
                          'AppointmentRecordForm.Fields.CareBoxOrder.careBoxType',
                        )}
                      </InputLabel>
                      <Select
                        labelId="care-box-select-label"
                        label="Pflegepaket"
                        value={selectedCareBox ?? ''}
                        onChange={(event) =>
                          setSelectedCareBox(Number(event.target.value))
                        }
                      >
                        {careBoxOptions(isAllowedToOrderCareBox).map(
                          (option) => (
                            <MenuItem
                              key={option.value}
                              value={option.value}
                              disabled={option.disabled}
                            >
                              {option.label}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  {!!selectedCareBox && selectedCareBox !== 5 && (
                    <Grid item xs={12}>
                      <Box className={classes.careBoxInfoBox}>
                        <Alert severity="info">
                          <Typography
                            className={classes.careBoxHeading}
                            gutterBottom
                          >
                            {t(
                              'AppointmentRecordForm.Fields.CareBox.includesHeading',
                            )}
                          </Typography>

                          <ul className={classes.careBoxList}>
                            {(
                              t(
                                `AppointmentRecordForm.Fields.CareBox.content.${selectedCareBox}`,
                                { returnObjects: true },
                              ) as string[]
                            ).map((line, idx) => (
                              <li key={idx}>
                                <Typography variant="body2">{line}</Typography>
                              </li>
                            ))}
                          </ul>
                        </Alert>
                      </Box>
                      <FormControl fullWidth>
                        {!signatureCareBox && (
                          <FormLabel
                            className={classes.signatureFieldLabel}
                            onClick={() =>
                              setModalOptions({
                                ...modalOptions,
                                description: t(
                                  'AppointmentRecordForm.Fields.SignatureCareBox.emptyCanvasField',
                                ),
                                onSuccess: (signature) =>
                                  setSignatureCareBox(signature),
                                open: true,
                                signatory: t(
                                  'AppointmentRecordForm.Fields.SignatureCareBox.helperText',
                                ),
                                title: t(
                                  'AppointmentRecordForm.Fields.SignatureCareBox.helperText',
                                ),
                              })
                            }
                          >
                            <AddIcon />
                            {t(
                              'AppointmentRecordForm.Fields.SignatureCareBox.label',
                            )}
                          </FormLabel>
                        )}

                        {signatureCareBox && (
                          <Box className={classes.signaturePreview}>
                            <Image src={signatureCareBox} />
                            <EditIcon
                              className={classes.editIcon}
                              onClick={() =>
                                setModalOptions({
                                  ...modalOptions,
                                  description: t(
                                    'AppointmentRecordForm.Fields.SignatureCareBox.emptyCanvasField',
                                  ),
                                  onSuccess: (signature) =>
                                    setSignatureCareBox(signature),
                                  open: true,
                                  signatory: t(
                                    'AppointmentRecordForm.Fields.SignatureCareBox.helperText',
                                  ),
                                  title: t(
                                    'AppointmentRecordForm.Fields.SignatureCareBox.helperText',
                                  ),
                                })
                              }
                            />
                          </Box>
                        )}
                      </FormControl>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Button
                        onClick={async () => {
                          let success;
                          try {
                            if (
                              !selectedCareBox ||
                              (selectedCareBox !== 5 && !signatureCareBox)
                            ) {
                              enqueueSnackbar(
                                t(
                                  'AppointmentRecordForm.Fields.SignatureCareBox.missingCareBoxSelection',
                                ),
                                { variant: 'error' },
                              );
                              return;
                            }
                            if (!customer) {
                              return;
                            }
                            if (selectedCareBox >= 1 && selectedCareBox <= 4) {
                              setIsOrderingCareBox(true);
                              await postCareBoxOrder({
                                careBoxType: selectedCareBox,
                                customerId: customer.id,
                                signatureCareBox: signatureCareBox || '',
                              });

                              enqueueSnackbar(
                                t(
                                  'AppointmentRecordForm.Fields.CareBoxOrder.orderSuccess',
                                ),
                                { variant: 'success' },
                              );
                              setShouldDisableCareBox(true);
                            } else if (selectedCareBox === 5) {
                              // redirect only for Box 5
                              const redirectQueryParameter =
                                await getCareBoxInfo({
                                  customerId: appointment.customerId,
                                });

                              success = window.open(
                                `https://hilfeimpaket-box.boxkonfigurator.de?prefill=${redirectQueryParameter.data}`,
                                '_blank',
                              );

                              if (success === null) {
                                enqueueSnackbar(
                                  t(
                                    'AppointmentRecordForm.Fields.CareBoxOrder.redirectError',
                                  ),
                                  { variant: 'error' },
                                );
                                throw Error(
                                  'Could not redirect, window.open responded with null.',
                                );
                              } else {
                                // set the carebox field on the customer
                                await postCareBoxOrder({
                                  careBoxType: selectedCareBox,
                                  customerId: customer.id,
                                  signatureCareBox: signatureCareBox || '',
                                });
                                enqueueSnackbar(
                                  t(
                                    'AppointmentRecordForm.Fields.CareBoxOrder.orderSuccess',
                                  ),
                                  { variant: 'success' },
                                );
                                setShouldDisableCareBox(false);
                              }
                            }
                          } catch (err: any) {
                            if (success !== undefined && success === null) {
                              // hopefully creates a sentry for more debug info
                              throw err;
                            }

                            const data = err.response?.data;

                            if (err.response?.status === 400 && data?.careBoxType) {
                              enqueueSnackbar(
                                data.careBoxType[0],
                                { variant: 'error' },
                              );
                            } else if (err.response?.status === 409) {
                              enqueueSnackbar(
                                data?.error ??
                                t(
                                  'AppointmentRecordForm.Fields.CareBoxOrder.alreadyOrdered',
                                ),
                                { variant: 'error' },
                              );
                              setShouldDisableCareBox(true);
                            } else if (data?.error_code === 'missing_mip_id') {
                              enqueueSnackbar(
                                t(
                                  'AppointmentRecordForm.Fields.CareBoxOrder.missingMipId',
                                ),
                                { variant: 'error' },
                              );
                            } else if (data?.error) {
                              enqueueSnackbar(data.error, { variant: 'error' });
                            } else {
                              Sentry.captureException(err, {
                                extra: {
                                  data,
                                  status: err.response?.status,
                                },
                              });
                              enqueueSnackbar(
                                t(
                                  'AppointmentRecordForm.Fields.CareBoxOrder.orderNotPossible',
                                ),
                                { variant: 'error' },
                              );
                            }
                          } finally {
                            setIsOrderingCareBox(false);
                          }
                        }}
                        disabled={isOrderingCareBox || shouldDisableCareBox}
                        loading={isOrderingCareBox}
                        color="secondary"
                        variant="contained"
                      >
                        {t('AppointmentRecordForm.submitOrder')}
                      </Button>
                    </Box>
                  </Grid>
                </>
              )}

              {values.wasFirstInterview && isSignable && (
                <>
                  <Grid item xs={12}>
                    <FormControl
                      data-test-id={ComponentTestIds.signatureDoaField}
                      fullWidth
                    >
                      {!values.signatureDoa && (
                        <FormLabel
                          className={classes.signatureFieldLabel}
                          onClick={() => goToSignatureModal('signatureDoa')}
                        >
                          <AddIcon />
                          {t('AppointmentRecordForm.Fields.SignatureDoa.label')}
                        </FormLabel>
                      )}

                      {values.signatureDoa && (
                        <Box className={classes.signaturePreview}>
                          <Image src={values.signatureDoa} />

                          <Typography className={classes.signatureName}>
                            {t(
                              'AppointmentRecordForm.Fields.SignatureDoa.helperText',
                            )}
                          </Typography>

                          <EditIcon
                            className={classes.editIcon}
                            onClick={() => goToSignatureModal('signatureDoa')}
                          />
                        </Box>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl
                      error={
                        !!(
                          touched.gdpr_main_consent && errors.gdpr_main_consent
                        )
                      }
                      fullWidth
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="gdpr_main_consent"
                            checked={values.gdpr_main_consent}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            color="primary"
                          />
                        }
                        label={t('SignatureGDPR.label', {
                          company: COMPANY_NAME,
                        })}
                      />
                      {touched.gdpr_main_consent &&
                        errors.gdpr_main_consent && (
                          <FormHelperText>
                            <ErrorIcon
                              fontSize="small"
                              style={{ marginRight: 4 }}
                            />
                            {errors.gdpr_main_consent}
                          </FormHelperText>
                        )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="gdpr_consent_partner_companies"
                          checked={values.gdpr_consent_partner_companies}
                          onChange={handleChange}
                          color="primary"
                        />
                      }
                      label={t('SignatureOptional.label', {
                        company: COMPANY_NAME,
                      })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl
                      data-test-id={ComponentTestIds.signatureDoppField}
                      error={!!(errors.signatureDopp && touched.signatureDopp)}
                      fullWidth
                    >
                      {!values.signatureDopp && (
                        <FormLabel
                          className={classes.signatureFieldLabel}
                          onClick={() => goToSignatureModal('signatureDopp')}
                        >
                          <AddIcon />
                          {t(
                            'AppointmentRecordForm.Fields.SignatureDopp.label',
                          )}
                        </FormLabel>
                      )}

                      {values.signatureDopp && (
                        <Box mb={2} className={classes.signaturePreview}>
                          <Image src={values.signatureDopp} />

                          <Typography className={classes.signatureName}>
                            {t(
                              'AppointmentRecordForm.Fields.SignatureDopp.helperText',
                            )}
                          </Typography>

                          <EditIcon
                            onClick={() => goToSignatureModal('signatureDopp')}
                            className={classes.editIcon}
                          />
                        </Box>
                      )}

                      {errors.signatureDopp && touched.signatureDopp && (
                        <FormHelperText>
                          <ErrorIcon /> {errors.signatureDopp}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </>
              )}

              {smsNotificationPhoneEnabled &&
                customer &&
                customer.smsNotificationPhone !== '' && (
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="center">
                      <Button
                        color="ternary"
                        onClick={() =>
                          setOpenSmsNotificationPhoneDialog(true)
                        }
                        type="button"
                        variant={
                          isSmsNotificationPhoneVerified ? 'text' : 'outlined'
                        }
                      >
                        {isSmsNotificationPhoneVerified
                          ? t(
                              'AppointmentRecordForm.SmsNotificationPhone.changeButton',
                              { phone: customer.smsNotificationPhone },
                            )
                          : t(
                              'AppointmentRecordForm.SmsNotificationPhone.openButton',
                            )}
                      </Button>
                    </Box>
                  </Grid>
                )}

              {isSignable && (
                <Grid item xs={12}>
                  <FormControl
                    data-test-id={ComponentTestIds.signatureEmployeeField}
                    error={
                      !!(
                        errors.signatureRecordEmployee &&
                        touched.signatureRecordEmployee
                      )
                    }
                    fullWidth
                  >
                    {!values.signatureRecordEmployee && (
                      <FormLabel
                        className={classes.signatureFieldLabel}
                        onClick={() =>
                          goToSignatureModal('signatureRecordEmployee')
                        }
                      >
                        <AddIcon />
                        {t(
                          'AppointmentRecordForm.Fields.SignatureRecordEmployee.label',
                        )}
                      </FormLabel>
                    )}

                    {values.signatureRecordEmployee && (
                      <Box className={classes.signaturePreview}>
                        <Image src={values.signatureRecordEmployee} />

                        <Typography className={classes.signatureName}>
                          {t(
                            'AppointmentRecordForm.Fields.SignatureRecordEmployee.helperText',
                          )}
                        </Typography>

                        <EditIcon
                          onClick={() =>
                            goToSignatureModal('signatureRecordEmployee')
                          }
                          className={classes.editIcon}
                        />
                      </Box>
                    )}

                    {errors.signatureRecordEmployee &&
                      touched.signatureRecordEmployee && (
                        <FormHelperText>
                          <ErrorIcon /> {errors.signatureRecordEmployee}
                        </FormHelperText>
                      )}
                  </FormControl>
                </Grid>
              )}

              {isSignable && (
                <Grid item xs={12}>
                  <FormControl
                    data-test-id={ComponentTestIds.signatureCustomerField}
                    error={
                      !!(
                        errors.signatureRecordCustomer &&
                        touched.signatureRecordCustomer
                      )
                    }
                    fullWidth
                  >
                    {!values.signatureRecordCustomer && (
                      <FormLabel
                        className={classes.signatureFieldLabel}
                        onClick={() =>
                          goToSignatureModal('signatureRecordCustomer')
                        }
                      >
                        <AddIcon />
                        {t(
                          'AppointmentRecordForm.Fields.SignatureRecordCustomer.label',
                        )}
                      </FormLabel>
                    )}

                    {values.signatureRecordCustomer && (
                      <Box mb={2} className={classes.signaturePreview}>
                        <Image src={values.signatureRecordCustomer} />

                        <Typography className={classes.signatureName}>
                          {t(
                            'AppointmentRecordForm.Fields.SignatureRecordCustomer.helperText',
                          )}
                        </Typography>

                        <EditIcon
                          onClick={() =>
                            goToSignatureModal('signatureRecordCustomer')
                          }
                          className={classes.editIcon}
                        />
                      </Box>
                    )}

                    {errors.signatureRecordCustomer &&
                      touched.signatureRecordCustomer && (
                        <FormHelperText>
                          <ErrorIcon /> {errors.signatureRecordCustomer}
                        </FormHelperText>
                      )}
                  </FormControl>
                </Grid>
              )}
            </Grid>
            {errors.submit && (
              <Box mt={3}>
                <Alert
                  data-test-id={ComponentTestIds.submitError}
                  severity="error"
                >
                  {errors.submit}
                </Alert>
              </Box>
            )}
            {!isSignable && (
              <Box mt={3}>
                <Alert
                  data-test-id={ComponentTestIds.infoText}
                  severity="error"
                >
                  <Typography>
                    {t('AppointmentRecordForm.notSignable')}
                  </Typography>
                </Alert>
              </Box>
            )}
            <Box mt={2} display="flex" justifyContent="center">
              <Button
                data-test-id={ComponentTestIds.submitButton}
                color="secondary"
                disabled={isSubmitting || !isSignable || showFutureWarning}
                loading={isSubmitting}
                size="large"
                type="submit"
                variant="contained"
              >
                {showFutureWarning
                  ? t('AppointmentRecordForm.lockedSubmitWarning')
                  : t('AppointmentRecordForm.submit')}
              </Button>
            </Box>
            <Signature
              onClose={() => {
                if (
                  modalOptions.title ===
                  t(
                    'AppointmentRecordForm.Fields.SignatureCareBox.helperText',
                  ) ||
                  modalOptions.signatory ===
                  t(
                    'AppointmentRecordForm.Fields.SignatureCareBox.helperText',
                  )
                ) {
                  setModalOptions((prev) => ({ ...prev, open: false }));
                } else {
                  history.goBack();
                }
              }}
              {...modalOptions}
            />
          </Form>
        )}
      </Formik>

      {customer && openSmsNotificationPhoneDialog && (
        <SmsNotificationPhoneDialog
          customerId={customer.id}
          initialPhone={
            customer.smsNotificationPhone || customer.phoneMobile1 || ''
          }
          onClose={() => setOpenSmsNotificationPhoneDialog(false)}
          startWithPhone={isSmsNotificationPhoneVerified}
        />
      )}

      {customer && (
        <NeedsAssessmentModal
          agreedActivitiesEnabled={agreedActivitiesEnabled}
          agreedActivitiesHourlyRateEnabled={agreedActivitiesHourlyRateEnabled}
          customer={customer}
          handleCancel={() => {
            formikRef.current?.setFieldValue('wasFirstInterview', false);
            setOpenNeedsAssessmentForm(false);
          }}
          handleClose={() => {
            setOpenNeedsAssessmentForm(false);
            setNeedsAssessmentFormSubmitted(true);
            formikRef.current?.setFieldValue('wasFirstInterview', true);
          }}
          open={openNeedsAssessmentForm}
        />
      )}
    </>
  );
};

export default observer(AppointmentRecordForm);
